import './registration-list-item.scss';

import { kebabCase } from 'lodash';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getRegistrationAddress } from '../../../data/data/registrations/utils';
import {
  RegistrationItemDataFragment,
  RegistrationStatus,
} from '../../../data/types/api/graphql';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { Tag, TagType } from '../../../elements/tag/tag';

const statusTagType: { [key in RegistrationStatus]: TagType } = {
  [RegistrationStatus.Open]: TagType.ORANGE,
  [RegistrationStatus.Accepted]: TagType.ACTIVE,
  [RegistrationStatus.Rejected]: TagType.NOTACTIVE,
};

type RegistrationListItemProps = {
  registration: RegistrationItemDataFragment;
  onClick?: (value: RegistrationItemDataFragment) => void;
};

/**
 * One row is one household. Person data never reaches this list — the list
 * query does not select `persons`, so a BSN cannot leak into an overview.
 */
export function RegistrationListItem({
  registration,
  onClick,
}: RegistrationListItemProps) {
  const intl = useIntl();

  const attributes = useMemo(() => {
    if (onClick) {
      return {
        role: 'button',
        tabIndex: 0,
        onClick: () => onClick(registration),
        onKeyDown: makeKeyDownHandler(() => onClick(registration)),
      };
    }

    return {};
  }, [registration, onClick]);

  return (
    <div
      {...attributes}
      className={`registration-list-item registration-list-item--${kebabCase(
        registration.status
      )}`}
    >
      <div className="registration-list-item__content">
        <h3>
          <CustomFormattedMessage id="registrations.submitted-at" />
        </h3>
        <p>
          {intl.formatDate(registration.submittedAt, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>

      <div className="registration-list-item__content registration-list-item__content--wide">
        <h3>
          <CustomFormattedMessage id="registrations.address" />
        </h3>
        <p>{getRegistrationAddress(registration)}</p>
      </div>

      <div className="registration-list-item__content">
        <h3>
          <CustomFormattedMessage id="registrations.persons" />
        </h3>
        <p>{registration.personCount}</p>
      </div>

      <div className="registration-list-item__tags">
        <Tag
          type={statusTagType[registration.status]}
          text={
            <CustomFormattedMessage
              id={`registrations.status.${kebabCase(registration.status)}`}
            />
          }
        />
      </div>
    </div>
  );
}
